import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/admin/verify?token=${token}`);
      if (response.data.valid) {
        setAdmin(response.data.admin);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/admin/login`, { email, password });
      if (response.data.success) {
        setToken(response.data.token);
        setAdmin(response.data.admin);
        localStorage.setItem('admin_token', response.data.token);
        return { success: true };
      }
    } catch (error) {
      console.error('Login failed:', error);
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Login failed' 
      };
    }
  };

  const logout = async () => {
    if (token) {
      try {
        await axios.post(`${API}/admin/logout?token=${token}`);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    setToken(null);
    setAdmin(null);
    localStorage.removeItem('admin_token');
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      const response = await axios.post(
        `${API}/admin/change-password?token=${token}&current_password=${currentPassword}&new_password=${newPassword}`
      );
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Failed to change password' 
      };
    }
  };

  return (
    <AdminContext.Provider value={{
      admin,
      token,
      loading,
      isAuthenticated: !!admin,
      login,
      logout,
      changePassword
    }}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;
